import { useEffect, useRef, useState } from 'react';
import type { ControlsState } from './Controls';
import { isRunActive, useInvalidateRunOutputs, useRuns, useTriggerRun } from './useHaloV2';

const cardCls =
  'rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 text-sm';

const STATE_COLORS: Record<string, string> = {
  completed: 'text-green-600',
  failed: 'text-red-600',
  active: 'text-blue-600',
  waiting: 'text-amber-600',
  delayed: 'text-amber-600',
  prioritized: 'text-blue-600',
  'waiting-children': 'text-amber-600',
};

export function RunsCard({ state }: { state: ControlsState }) {
  const [mode, setMode] = useState<'incremental' | 'backfill'>('backfill');
  const runsQuery = useRuns();
  const trigger = useTriggerRun();
  const invalidateOutputs = useInvalidateRunOutputs();
  const prevActiveIds = useRef<Set<string>>(new Set());

  // When a previously-active run reaches a terminal state (or is removed by removeOnComplete), refresh chart data.
  useEffect(() => {
    const runs = runsQuery.data ?? [];
    const nowActive = new Set(runs.filter(isRunActive).map((r) => r.id));
    const completed = [...prevActiveIds.current].some((id) => !nowActive.has(id));
    prevActiveIds.current = nowActive;
    if (completed) invalidateOutputs();
  }, [runsQuery.data, invalidateOutputs]);

  const onRun = () => {
    const body =
      mode === 'backfill'
        ? { mode, storeIds: [state.storeId], startDate: state.start, endDate: state.end }
        : { mode, storeIds: [state.storeId] };
    const desc =
      mode === 'backfill'
        ? `Backfill store ${state.storeId}, ${state.start} to ${state.end}?`
        : `Incremental run for store ${state.storeId} (trailing 3 days)?`;
    if (window.confirm(desc)) trigger.mutate(body);
  };

  return (
    <div className={cardCls}>
      <h3 className="font-medium text-gray-700 dark:text-gray-200 mb-2">Runs</h3>
      <div className="flex items-center gap-2 mb-2">
        <select
          className="rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1 text-xs"
          value={mode}
          onChange={(e) => setMode(e.target.value === 'incremental' ? 'incremental' : 'backfill')}
        >
          <option value="backfill">Backfill (current range)</option>
          <option value="incremental">Incremental (3d)</option>
        </select>
        <button
          type="button"
          disabled={state.storeId === 0 || trigger.isPending}
          onClick={onRun}
          className="rounded-md bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 px-3 py-1 text-xs font-medium disabled:opacity-50"
        >
          Run
        </button>
      </div>
      {trigger.isError ? (
        <p className="text-xs text-red-500 mb-2">
          {trigger.error?.message ?? 'Run trigger failed'}
        </p>
      ) : null}
      <div className="space-y-1.5 max-h-56 overflow-y-auto">
        {(runsQuery.data ?? []).map((run) => (
          <div
            key={run.id}
            className="text-xs border-t border-gray-100 dark:border-gray-800 pt-1.5"
          >
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-300">
                {run.params?.mode ?? run.name}
                {run.params?.startDate
                  ? ` ${run.params.startDate} to ${run.params.endDate ?? ''}`
                  : ''}
              </span>
              <span className={STATE_COLORS[run.state] ?? 'text-gray-400'}>{run.state}</span>
            </div>
            {run.result ? (
              <span className="text-gray-400">
                {run.result.rowsWritten.toLocaleString()} rows
                {run.result.errors.length > 0 ? ` (${run.result.errors.length} store errors)` : ''}
              </span>
            ) : null}
            {run.failedReason ? <span className="text-red-500">{run.failedReason}</span> : null}
            {run.result && run.result.errors.length > 0 ? (
              <details className="text-gray-400">
                <summary>errors</summary>
                {run.result.errors.map((e, i) => (
                  <div key={i}>
                    store {e.storeId} ({e.platform}): {e.error}
                  </div>
                ))}
              </details>
            ) : null}
          </div>
        ))}
        {(runsQuery.data ?? []).length === 0 ? (
          <p className="text-xs text-gray-400">No runs yet.</p>
        ) : null}
      </div>
    </div>
  );
}
